// /app/taxonomy — in-app reference for the active naming-convention
// ruleset (see `src/shared/utm/naming-conventions.ts`). Renders the
// `Ruleset` object directly so it can never drift from the scorer's
// view: every list, every regex, every banned-vague substring is the
// same data the audit grades against.

import { useEffect, useState } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  CANONICAL_MEDIUMS,
  CANONICAL_PLATFORMS,
  SOURCE_TO_PLATFORM,
  type CanonicalPlatform,
} from "@shared/utm/types";
import { loadRuleset, type FormatRuleId } from "@shared/utm/naming-conventions";
import { fetchBootstrap, type BootstrapResponse } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";

// Taxonomy is purely static reference content — accessible without
// auth. We still bootstrap to surface the connected property in the
// shell (for authed users), but anon visitors render the same page
// with no property chip in the header.
type Status = { kind: "loading" } | { kind: "ready"; bootstrap: BootstrapResponse | null };

const MEDIUM_COPY: Record<(typeof CANONICAL_MEDIUMS)[number], { summary: string; when: string }> = {
  "organic-social": {
    summary: "Unpaid posts from a brand-owned account.",
    when: "Your scheduler posts to IG / TikTok / LinkedIn. No spend behind it.",
  },
  "paid-social": {
    summary: "Promoted posts and ads where you're paying a platform.",
    when: "Meta Ads, LinkedIn Ads, TikTok Ads Manager, boosted posts.",
  },
  influencer: {
    summary: "Third-party creators posting on your behalf.",
    when: "A creator shares a link in their story or bio — paid or gifted, but not through an ad platform.",
  },
  community: {
    summary: "Links shared inside a community the brand doesn't own.",
    when: "A mod drops a link in a Discord, Slack, or Reddit the brand participates in.",
  },
};

const FORMAT_RULE_COPY: Record<FormatRuleId, { do: string; dont: string }> = {
  lowercase: {
    do: "google · linkedin · q1-free-trial",
    dont: "Google · LinkedIn · Q1-Free-Trial",
  },
  "hyphens-not-spaces": {
    do: "brand-awareness · q2-2025",
    dont: "brand awareness · q2 2025",
  },
  "no-underscores": {
    do: "q4-launch · sku-1234-red",
    dont: "q4_launch · sku_1234_red",
  },
  "no-special-chars": {
    do: "q1-2025 · partner-geekout",
    dont: "q1+2025 · partner&geekout",
  },
};

export function TaxonomyPage() {
  usePageMeta({
    title: "Taxonomy · Traffic Snapshot",
    description:
      "Reference for the active UTM naming convention — the shape the audit scores against.",
  });
  const [status, setStatus] = useState<Status>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const bootstrap = await fetchBootstrap();
        if (cancelled) return;
        setStatus({
          kind: "ready",
          bootstrap: bootstrap.authenticated ? bootstrap : null,
        });
      } catch {
        if (!cancelled) setStatus({ kind: "ready", bootstrap: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const property = status.kind === "ready" ? (status.bootstrap?.defaultProperty ?? null) : null;
  const ruleset = loadRuleset();

  // Group synonyms by canonical platform so each platform row shows the
  // variants we'll collapse to it.
  const synonymsByPlatform = new Map<CanonicalPlatform, string[]>();
  for (const [synonym, canonical] of Object.entries(SOURCE_TO_PLATFORM)) {
    const list = synonymsByPlatform.get(canonical) ?? [];
    if (synonym.toLowerCase() !== canonical.toLowerCase()) list.push(synonym);
    synonymsByPlatform.set(canonical, list);
  }

  return (
    <AppShell
      property={property ?? null}
      eyebrow="Taxonomy"
      title={
        <>
          How Sked thinks about <em className="font-serif text-purple-700">UTMs</em>
        </>
      }
      subtitle={`Active preset: ${ruleset.id} · v${ruleset.version}. The audit scores every URL against this shape — keep tags inside it and your reports read cleanly.`}
    >
      <div className="space-y-12">
        <section id="why" className="scroll-mt-24 space-y-3">
          <SectionHeader
            eyebrow="Why a convention"
            title={
              <>
                Opinionated beats <em className="font-serif text-purple-700">freeform</em>
              </>
            }
          />
          <div className="rounded-2xl bg-white p-6 ring-1 ring-neutral-950/5">
            <p className="max-w-[70ch] text-sm text-neutral-700 text-pretty">
              Analytics tools will happily report whatever you put in <code>utm_medium</code> and{" "}
              <code>utm_source</code>. The cost is that <strong>"Instagram"</strong>,{" "}
              <strong>"instagram"</strong>, <strong>"IG"</strong> and{" "}
              <strong>"instagram.com"</strong> land as four different lines. Over a quarter you end
              up comparing fragments of the same channel rather than the channel itself.
            </p>
            <p className="mt-3 max-w-[70ch] text-sm text-neutral-700 text-pretty">
              The naming convention codifies four things: global formatting (lowercase + hyphens, no
              special chars), a controlled vocabulary for <code>utm_source</code> and{" "}
              <code>utm_medium</code>, campaign-name patterns to favour, and an anti-pattern list of
              vague names to avoid. The audit grades every tag against this shape, the UTM builder
              emits it by default, and the leaderboard groups by it.
            </p>
          </div>
        </section>

        <section id="formatting" className="scroll-mt-24 space-y-3">
          <SectionHeader
            eyebrow="Global formatting"
            title={
              <>
                Four <em className="font-serif text-purple-700">mechanical</em> rules
              </>
            }
          />
          <ul role="list" className="grid gap-3 md:grid-cols-2">
            {ruleset.formatRules.map((id) => {
              const copy = FORMAT_RULE_COPY[id];
              return (
                <li key={id} className="rounded-2xl bg-white p-5 ring-1 ring-neutral-950/5">
                  <code className="inline-block rounded-md bg-sky-50 px-2 py-0.5 font-mono text-[13px] font-semibold text-sky-700 ring-1 ring-sky-200">
                    {id}
                  </code>
                  <dl className="mt-3 space-y-2 text-sm">
                    <div>
                      <dt className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                        Do
                      </dt>
                      <dd className="mt-0.5 font-mono text-xs text-neutral-700">{copy.do}</dd>
                    </div>
                    <div>
                      <dt className="text-[11px] font-semibold uppercase tracking-wide text-rose-700">
                        Don't
                      </dt>
                      <dd className="mt-0.5 font-mono text-xs text-neutral-500 line-through">
                        {copy.dont}
                      </dd>
                    </div>
                  </dl>
                </li>
              );
            })}
          </ul>
        </section>

        <section id="source-vocab" className="scroll-mt-24 space-y-3">
          <SectionHeader
            eyebrow="Source vocabulary"
            title={
              <>
                Allowed values for <code className="font-serif text-purple-700">utm_source</code>
              </>
            }
          />
          <div className="rounded-2xl bg-white p-6 ring-1 ring-neutral-950/5">
            <div className="flex flex-wrap gap-1.5">
              {ruleset.source.allowlist.map((src) => (
                <code
                  key={src}
                  className="rounded-md bg-violet-50 px-2 py-0.5 font-mono text-xs font-medium text-violet-700 ring-1 ring-violet-200"
                >
                  {src}
                </code>
              ))}
            </div>
            {ruleset.source.partnerPrefix && (
              <p className="mt-4 text-xs text-neutral-600">
                <strong>Plus any value with the prefix </strong>
                <code className="rounded bg-neutral-100 px-1 py-0.5 font-mono text-[11px]">
                  partner-
                </code>{" "}
                — e.g. <code>partner-geekout</code>, <code>partner-twism</code>. Always include the
                partner name; never use bare <code>partner</code>.
              </p>
            )}
          </div>
        </section>

        <section id="medium-vocab" className="scroll-mt-24 space-y-3">
          <SectionHeader
            eyebrow="Medium vocabulary"
            title={
              <>
                Allowed values for <code className="font-serif text-purple-700">utm_medium</code>
              </>
            }
          />
          <div className="rounded-2xl bg-white p-6 ring-1 ring-neutral-950/5">
            <div className="flex flex-wrap gap-1.5">
              {ruleset.medium.allowlist.map((m) => (
                <code
                  key={m}
                  className="rounded-md bg-violet-50 px-2 py-0.5 font-mono text-xs font-medium text-violet-700 ring-1 ring-violet-200"
                >
                  {m}
                </code>
              ))}
            </div>
            {ruleset.medium.tooGeneric.length > 0 && (
              <p className="mt-4 text-xs text-neutral-600">
                <strong>Permitted but generic:</strong>{" "}
                {ruleset.medium.tooGeneric.map((g) => (
                  <code key={g} className="rounded bg-amber-50 px-1 py-0.5 font-mono text-[11px]">
                    {g}
                  </code>
                ))}
                . The audit fires a mild "be more specific" warning — prefer{" "}
                <code>organic-social</code> or <code>paid-social</code> for tagged posts.
              </p>
            )}
          </div>
        </section>

        <section id="mediums-strict" className="scroll-mt-24 space-y-3">
          <SectionHeader
            eyebrow="Sked-stricter tier"
            title={
              <>
                The four <em className="font-serif text-purple-700">leaderboard</em> mediums
              </>
            }
          />
          <p className="max-w-[70ch] text-sm text-neutral-700 text-pretty">
            On top of the broad medium vocabulary above, four values feed the leaderboard's
            per-channel grouping. They're the most useful framing for a social-traffic snapshot —
            tag every link with one of these four when it makes sense.
          </p>
          <ul role="list" className="grid gap-3 md:grid-cols-2">
            {CANONICAL_MEDIUMS.map((m) => {
              const copy = MEDIUM_COPY[m];
              return (
                <li key={m} className="rounded-2xl bg-white p-5 ring-1 ring-neutral-950/5">
                  <code className="inline-block rounded-md bg-purple-50 px-2 py-0.5 font-mono text-[13px] font-semibold text-purple-700 ring-1 ring-purple-200">
                    {m}
                  </code>
                  <p className="mt-3 text-sm font-semibold text-neutral-950">{copy.summary}</p>
                  <p className="mt-1 text-sm text-neutral-600">{copy.when}</p>
                </li>
              );
            })}
          </ul>
        </section>

        <section id="platforms" className="scroll-mt-24 space-y-3">
          <SectionHeader
            eyebrow="Platforms"
            title={
              <>
                Canonical <em className="font-serif text-purple-700">platforms</em> & their synonyms
              </>
            }
          />
          <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-neutral-950/5">
            <table className="w-full">
              <thead className="bg-neutral-50">
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold text-neutral-700"
                  >
                    Canonical
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold text-neutral-700"
                  >
                    Synonyms that roll up to it
                  </th>
                </tr>
              </thead>
              <tbody>
                {CANONICAL_PLATFORMS.map((platform, idx) => (
                  <tr
                    key={platform}
                    className={
                      idx < CANONICAL_PLATFORMS.length - 1
                        ? "border-b border-neutral-100"
                        : undefined
                    }
                  >
                    <td className="px-4 py-3 align-top">
                      <span className="font-semibold text-neutral-950">{platform}</span>
                    </td>
                    <td className="px-4 py-3 align-top">
                      {synonymsByPlatform.get(platform)?.length ? (
                        <div className="flex flex-wrap gap-1.5">
                          {synonymsByPlatform.get(platform)!.map((s) => (
                            <code
                              key={s}
                              className="rounded-md bg-neutral-100 px-1.5 py-0.5 font-mono text-[11px] text-neutral-700"
                            >
                              {s}
                            </code>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-neutral-400">
                          (canonical form only — use exactly as written)
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-neutral-500">
            Anything the scorer can't map to one of the above canonical platforms collapses to{" "}
            <strong>Other Social</strong>. The audit flags the original value so you can decide
            whether to add it to the map or clean up the tag.
          </p>
        </section>

        <section id="campaign" className="scroll-mt-24 space-y-3">
          <SectionHeader
            eyebrow="Campaign naming"
            title={
              <>
                Keep campaigns <em className="font-serif text-purple-700">groupable</em>
              </>
            }
          />
          <div className="rounded-2xl bg-white p-6 ring-1 ring-neutral-950/5">
            <p className="max-w-[70ch] text-sm text-neutral-700">
              No fixed shape — but a few patterns read well in a leaderboard:
            </p>
            <ul role="list" className="mt-3 space-y-1.5 text-sm text-neutral-700">
              <li>
                <code className="rounded bg-neutral-50 px-1.5 py-0.5 font-mono text-xs">
                  q1-free-trial
                </code>{" "}
                — quarter + objective.
              </li>
              <li>
                <code className="rounded bg-neutral-50 px-1.5 py-0.5 font-mono text-xs">
                  apr-newsletter
                </code>{" "}
                — month + format.
              </li>
              <li>
                <code className="rounded bg-neutral-50 px-1.5 py-0.5 font-mono text-xs">
                  blackfriday-2025-sale
                </code>{" "}
                — event + year + type.
              </li>
              <li>
                <code className="rounded bg-neutral-50 px-1.5 py-0.5 font-mono text-xs">
                  scheduling-feature-launch
                </code>{" "}
                — product + initiative.
              </li>
            </ul>
            {ruleset.campaign.bannedSubstrings.length > 0 && (
              <>
                <p className="mt-5 text-sm font-semibold text-neutral-950">Avoid (anti-pattern):</p>
                <p className="mt-1 max-w-[70ch] text-xs text-neutral-600">
                  Generic / placeholder names — they survive forever and pile up in the leaderboard.
                  The audit fires <code>anti-pattern</code> on any campaign containing one of these.
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {ruleset.campaign.bannedSubstrings.slice(0, 24).map((b) => (
                    <code
                      key={b}
                      className="rounded-md bg-rose-50 px-1.5 py-0.5 font-mono text-[11px] text-rose-700 ring-1 ring-rose-200"
                    >
                      {b}
                    </code>
                  ))}
                  {ruleset.campaign.bannedSubstrings.length > 24 && (
                    <span className="text-[11px] text-neutral-500">
                      +{ruleset.campaign.bannedSubstrings.length - 24} more
                    </span>
                  )}
                </div>
              </>
            )}
            <p className="mt-5 text-xs text-neutral-500">
              Campaign names shorter than 4 characters trip the "Short campaign" flag.
              YYYYMMDD-style dates (e.g. <code>20250401</code>) are flagged as anti-pattern — prefer{" "}
              <code>apr-2025</code> or <code>q2-2025</code>.
            </p>
          </div>
        </section>

        <section id="next" className="scroll-mt-24 space-y-3">
          <SectionHeader
            eyebrow="Next"
            title={
              <>
                Go <em className="font-serif text-purple-700">tag</em> something
              </>
            }
          />
          <div className="flex flex-wrap gap-3">
            <Button asChild>
              <Link to="/app/builder">
                Open UTM builder
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/app/utm-health">
                See what's flagged
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </section>
      </div>
    </AppShell>
  );
}

function SectionHeader({ eyebrow, title }: { eyebrow: string; title: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wide text-purple-700">{eyebrow}</p>
      <span aria-hidden="true" className="mt-1.5 block h-[3px] w-8 rounded-full bg-purple-500" />
      <h2 className="mt-3 font-display text-2xl font-semibold text-neutral-950">{title}</h2>
    </div>
  );
}
